"use client";

import React, { useRef, useEffect } from "react";
import { Skeleton } from "../ui/skeleton";

interface OtpInputProps {
  numDigits: number;
  value: string;
  onChange: (otp: string) => void;
  resendInSecs: number | undefined;
  onResend?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  numDigits,
  value,
  onChange,
  resendInSecs,
  onResend,
  disabled = false,
  autoFocus = false,
  className = "",
}) => {
  const [resending, setResending] = React.useState(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  // Split value into array for rendering
  const otpArray = Array.from({ length: numDigits }, (_, i) => value[i] || "");

  // Focus first input on mount if autoFocus
  useEffect(() => {
    if (autoFocus && inputs.current[0]) {
      inputs.current[0].focus();
    }
    // eslint-disable-next-line
  }, []);

  const handleChange = (idx: number, val: string) => {
    // If user pasted or autofilled multiple characters
    if (val.length > 1) {
      const paste = val.replace(/\D/g, "").slice(0, numDigits);

      // If it's a full or partial OTP injected into the first box (e.g. Autofill)
      if (idx === 0 || paste.length === numDigits) {
        if (paste) {
          onChange(paste);
          const nextIdx = Math.min(paste.length, numDigits - 1);
          inputs.current[nextIdx]?.focus();
        }
        return;
      }

      // Otherwise, the user probably typed a digit into an already populated box.
      // We take the last typed character.
      val = val.slice(-1);
    }

    if (!/^[0-9]?$/.test(val)) return;
    const arr = [...otpArray];
    arr[idx] = val;
    const newOtp = arr.join("");
    onChange(newOtp);
    if (val && idx < numDigits - 1) {
      inputs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpArray[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, numDigits);
    if (paste) {
      onChange(paste);
      const nextIdx = Math.min(paste.length, numDigits - 1);
      inputs.current[nextIdx]?.focus();
    }
  };

  return (
    <div className={className}>
      <div className="flex gap-4 justify-center">
        {Array.from({ length: numDigits }).map((_, i) => (
          <input
            key={i}
            type="text"
            maxLength={numDigits}
            className="size-10 text-lg font-bold text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
            value={otpArray[i]}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            autoFocus={autoFocus && i === 0}
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={disabled}
            onPaste={handlePaste}
          />
        ))}
      </div>
      <ResendButton
        onResend={
          onResend
            ? () => {
                setResending(true);
                onResend();
                setTimeout(() => setResending(false), 1000);
              }
            : undefined
        }
        resendInSecs={resendInSecs}
        resending={resending}
      />
    </div>
  );
};

function ResendButton({
  onResend,
  resendInSecs,
  resending,
}: {
  onResend?: () => void;
  resendInSecs?: number;
  resending: boolean;
}) {
  return (
    typeof onResend === "function" && (
      <div className="flex items-center mt-6">
        {resendInSecs === undefined ? (
          <Skeleton className="h-5 w-32" />
        ) : resendInSecs > 0 ? (
          <span className="text-gray-500 text-sm">
            {`Send code again in 00:${resendInSecs.toString().padStart(2, "0")}`}{" "}
          </span>
        ) : (
          <button
            type="button"
            className="text-blue-600 text-sm font-medium disabled:text-gray-400 cursor-pointer"
            onClick={onResend}
          >
            {resending ? "Resending..." : "Resend OTP"}
          </button>
        )}
      </div>
    )
  );
}
